import React from 'react';
import { Upload } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { PdfPreviewModal } from '../../templates/components/PdfPreviewModal';
import {
  BusinessDocumentsList,
  type BusinessDocumentFolderOption,
  type BusinessDocumentItem,
} from './BusinessDocumentsList';
import { isBusinessDocumentPdf } from '../utils/documentVisuals';

const ACCEPTED_FILE_TYPES =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*,text/plain,text/csv';

async function parseErrorResponse(res: Response, fallback: string) {
  const text = await res.text().catch(() => '');
  if (!text.trim()) return `${fallback} (HTTP ${res.status})`;
  try {
    const parsed = JSON.parse(text) as { error?: string; details?: string };
    if (parsed.details) return `${parsed.error || fallback} (${parsed.details})`;
    return parsed.error || fallback;
  } catch {
    return text;
  }
}

type BusinessDocumentsSectionProps = {
  selectedFolderId: string | null;
  selectedFolderName: string | null;
  folders: BusinessDocumentFolderOption[];
  onRefreshFolders: () => Promise<void>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onUploadClick: () => void;
};

export function BusinessDocumentsSection({
  selectedFolderId,
  selectedFolderName,
  folders,
  onRefreshFolders,
  fileInputRef,
  onUploadClick,
}: BusinessDocumentsSectionProps) {
  const [documents, setDocuments] = React.useState<BusinessDocumentItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = React.useState<string | null>(null);
  const [downloadingDocumentId, setDownloadingDocumentId] = React.useState<string | null>(null);
  const [editingDocumentId, setEditingDocumentId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editFolderId, setEditFolderId] = React.useState('');
  const [savingEditDocumentId, setSavingEditDocumentId] = React.useState<string | null>(null);
  const [pendingDeleteDocument, setPendingDeleteDocument] = React.useState<BusinessDocumentItem | null>(null);
  const [previewingDocumentId, setPreviewingDocumentId] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<{ url: string; fileName: string } | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);

  const loadDocuments = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = selectedFolderId ? `?folder_id=${encodeURIComponent(selectedFolderId)}` : '';
      const res = await fetch(`/api/business-documents${params}`);
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Dokumente konnten nicht geladen werden.'));
      const data = (await res.json()) as BusinessDocumentItem[];
      setDocuments(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Dokumente konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [selectedFolderId]);

  React.useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('display_name', file.name);
      if (selectedFolderId) formData.append('folder_id', selectedFolderId);
      const res = await fetch('/api/business-documents/upload', { method: 'POST', body: formData });
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Upload fehlgeschlagen.'));
      await loadDocuments();
      await onRefreshFolders();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
    }
  };

  const startEditDocument = (doc: BusinessDocumentItem) => {
    setEditingDocumentId(doc.id);
    setEditName(doc.display_name || doc.filename);
    setEditFolderId(doc.folder_id || '');
  };

  const cancelEditDocument = () => {
    setEditingDocumentId(null);
    setEditName('');
    setEditFolderId('');
  };

  const saveEditDocument = async (doc: BusinessDocumentItem) => {
    setSavingEditDocumentId(doc.id);
    setActionError(null);
    try {
      const res = await fetch(`/api/business-documents/${encodeURIComponent(doc.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: editName,
          folder_id: editFolderId || null,
        }),
      });
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Speichern fehlgeschlagen.'));
      cancelEditDocument();
      await loadDocuments();
      await onRefreshFolders();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSavingEditDocumentId(null);
    }
  };

  const downloadDocument = async (doc: BusinessDocumentItem) => {
    setDownloadingDocumentId(doc.id);
    try {
      window.open(`/api/business-documents/${encodeURIComponent(doc.id)}/download`, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Download fehlgeschlagen.');
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const previewDocument = async (doc: BusinessDocumentItem) => {
    if (!isBusinessDocumentPdf(doc.filename, doc.mime_type)) return;
    setPreviewingDocumentId(doc.id);
    setPreviewError(null);
    try {
      const res = await fetch(`/api/business-documents/${encodeURIComponent(doc.id)}/signed-url`);
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Vorschau konnte nicht geladen werden.'));
      const data = (await res.json()) as { url?: string; filename?: string };
      if (!data.url) throw new Error('Vorschau konnte nicht geladen werden.');
      setPreview({
        url: data.url,
        fileName: doc.display_name || data.filename || doc.filename,
      });
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Vorschau konnte nicht geladen werden.');
    } finally {
      setPreviewingDocumentId(null);
    }
  };

  const closePreview = () => {
    setPreview(null);
  };

  const confirmDeleteDocument = async () => {
    if (!pendingDeleteDocument) return;
    setDeletingDocumentId(pendingDeleteDocument.id);
    setActionError(null);
    try {
      const res = await fetch(`/api/business-documents/${encodeURIComponent(pendingDeleteDocument.id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Löschen fehlgeschlagen.'));
      setPendingDeleteDocument(null);
      await loadDocuments();
      await onRefreshFolders();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeletingDocumentId(null);
    }
  };

  return (
    <>
      <div className="hidden md:block rounded-xl border border-app-border bg-app-surface px-5 py-4">
        <div className="flex items-center justify-between gap-8 lg:gap-12">
          <div className="flex-1 min-w-0 pr-2">
            <h2 className="text-base font-semibold text-app-text">Geschäftsunterlagen</h2>
            <p className="text-sm text-app-text-subtle mt-1 leading-relaxed">
              Allgemeine Firmendokumente hochladen und in Ordnern verwalten.
            </p>
            <p className="text-xs text-app-text-subtle mt-3">
              <span className="text-slate-400">Zielordner:</span>{' '}
              <span className="font-medium text-app-text-muted">{selectedFolderName || 'Kein Ordner'}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onUploadClick}
              disabled={uploading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-pipedrive-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? (
                <LoadingSpinner className="shrink-0" />
              ) : (
                <Upload className="h-4 w-4 shrink-0" aria-hidden />
              )}
              {uploading ? 'Hochladen...' : 'Dokument hochladen'}
            </button>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        onChange={onFileChange}
        className="hidden"
      />

      {uploadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {uploadError}
        </div>
      ) : null}

      {previewError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {previewError}
        </div>
      ) : null}

      {actionError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {actionError}
        </div>
      ) : null}

      <BusinessDocumentsList
        documents={documents}
        folders={folders}
        loading={loading}
        error={error}
        editingDocumentId={editingDocumentId}
        editName={editName}
        editFolderId={editFolderId}
        setEditName={setEditName}
        setEditFolderId={setEditFolderId}
        onStartEdit={startEditDocument}
        onSaveEdit={(doc) => void saveEditDocument(doc)}
        onCancelEdit={cancelEditDocument}
        savingEditDocumentId={savingEditDocumentId}
        onDelete={(doc) => setPendingDeleteDocument(doc)}
        deletingDocumentId={deletingDocumentId}
        onDownload={(doc) => void downloadDocument(doc)}
        downloadingDocumentId={downloadingDocumentId}
        onPreview={(doc) => void previewDocument(doc)}
        previewingDocumentId={previewingDocumentId}
      />

      <PdfPreviewModal
        open={Boolean(preview)}
        fileUrl={preview?.url || null}
        fileName={preview?.fileName || 'dokument.pdf'}
        onClose={closePreview}
      />

      {pendingDeleteDocument && (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/40"
          onClick={() => !deletingDocumentId && setPendingDeleteDocument(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl border border-app-border bg-app-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-business-doc-modal-title"
          >
            <div className="mb-4">
              <h2 id="delete-business-doc-modal-title" className="text-base font-bold text-app-text">
                Dokument löschen
              </h2>
            </div>
            <p className="text-sm text-app-text-muted leading-relaxed">
              Möchtest du{' '}
              <span className="font-semibold">
                {pendingDeleteDocument.display_name || pendingDeleteDocument.filename}
              </span>{' '}
              wirklich löschen?
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDeleteDocument(null)}
                disabled={Boolean(deletingDocumentId)}
                className="btn-pipedrive-secondary"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void confirmDeleteDocument()}
                disabled={Boolean(deletingDocumentId)}
                className="app-btn-danger-outline inline-flex items-center justify-center rounded-lg border border-red-200 bg-app-surface px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {deletingDocumentId ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
